import PropTypes from 'prop-types';
import {Form, Select} from 'antd';
import {connect} from 'react-redux';
import {SystemSelectNames} from '../SystemSelectEntityEnum';
import {isArray, isEmpty} from 'lodash';
import {RQCommonListData} from '@app/reactQuery/apis';
import {selectAuth} from '@app/redux/selectors';
import {useEffect, useRef, useState} from 'react';
import {SortOptions} from '@app/components/common/Filters/SortOptions';

const SingleSelectDependentObjectAttribute = ({auth, attributeMetadata, formValues, updateDynamicFieldValue}) => {
  const form = Form.useFormInstance();
  const isFirstMount = useRef(true);
  const prevParentIdRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');

  const rawParent = formValues?.[attributeMetadata?.input_parent_id];
  const parentId = rawParent && typeof rawParent === 'object' ? rawParent?.id : rawParent;

  const selectEnum = SystemSelectNames[attributeMetadata?.select_entity];

  const updatedMisc = {...selectEnum.misc};
  Object.keys(updatedMisc).forEach(key => {
    const value = updatedMisc[key];
    if (value.startsWith('#') && value.endsWith('#')) {
      updatedMisc[key] = parentId;
    }
  });
  const {data} =
      RQCommonListData(
        {
          misc: updatedMisc,
          name: selectEnum?.name,
          api: selectEnum?.api,
          certificate: auth?.certificate,
          userId: auth?.principal?.id,
          locationId: auth?.user?.location?.id,
          filter: {
            ...selectEnum?.filter ||
            (attributeMetadata?.input_search_enable ? {[attributeMetadata.input_search_field_name]: searchValue} : {}),
          },
        }, {
          enabled: !!parentId && (!attributeMetadata?.input_search_enable || searchValue.length > 2),
        });

  const dependentData = (attributeMetadata?.input_search_enable && searchValue.length < 3)
    ? []
    : isArray(data)
      ? data
      : data
        ? [data]
        : [];

  useEffect(() => {
    const currentParentVal = form.getFieldValue(attributeMetadata?.input_parent_id);
    const currentParentId =
        currentParentVal && typeof currentParentVal === 'object'
          ? currentParentVal?.id
          : currentParentVal;

    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevParentIdRef.current = currentParentId;
      return;
    }

    if (prevParentIdRef.current === currentParentId) {
      return;
    }

    prevParentIdRef.current = currentParentId;

    const timeout = setTimeout(() => {
      form.setFieldValue(attributeMetadata?.id, undefined);
      updateDynamicFieldValue?.({[attributeMetadata?.id]: undefined});
    }, 150);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue(attributeMetadata?.input_parent_id)]);

  useEffect(() => {
    const externalVal = formValues?.[attributeMetadata?.id];
    const formVal = form.getFieldValue(attributeMetadata?.id);

    if (externalVal !== undefined && externalVal !== formVal) {
      form.setFieldValue(attributeMetadata?.id, externalVal);
    }
  }, [formValues, attributeMetadata?.id, form]);

  const isVertical = attributeMetadata?.layout === 'vertical';
  return (
    <Form.Item
      name={[attributeMetadata.id]}
      label={attributeMetadata.label}
      wrapperCol={{
        span: isVertical
          ? ((isVertical && attributeMetadata?.vertical_value_col_span)
            ? attributeMetadata?.vertical_value_col_span
            : 24)
          : 20,
      }}
      labelCol={{
        span: isVertical ? 24 : 4,
        ...((isVertical && attributeMetadata?.vertical_label_height)
          ? {style: {height: attributeMetadata?.vertical_label_height}}
          : {}),
      }}
      rules={attributeMetadata.validation_rules}
    >

      <Select
        allowClear={true}
        // mode={attributeMetadata.multiple === true ? 'multiple' : 'single'}
        filterOption={(input, option) =>
          option.children?.filter(c => c !== undefined && c.toLowerCase().indexOf(input?.toLowerCase()) >= 0)[0]
        }
        showSearch={attributeMetadata.show_search}
        placeholder={attributeMetadata?.placeholder}
        disabled={!attributeMetadata.editable}
        // onChange={obj => {
        //   const fieldLabelValue = dependentData.filter(val => val?.id === obj)[0]?.name;
        //   if (form.getFieldValue(attributeMetadata.id + '_label') !== fieldLabelValue) {
        //     form.setFieldValue(attributeMetadata.id + '_label', fieldLabelValue);
        //   }
        // }}
        onChange={ value => {
          console.log(value);
          form.setFieldValue(attributeMetadata?.id, value);
          const updatedValues = {[attributeMetadata?.id]: value};
          if (!isEmpty(attributeMetadata?.reset_child_values_on_change)) {
            attributeMetadata?.reset_child_values_on_change?.forEach(childId => {
              updatedValues[childId] = undefined;
              form.setFieldValue(childId, undefined);
            });
            updateDynamicFieldValue(updatedValues);
          } else {
            const fieldLabelValue = dependentData.filter(val => val?.id === value)[0]?.name;
            if (form.getFieldValue(attributeMetadata.id + '_label') !== fieldLabelValue) {
              form.setFieldValue(attributeMetadata.id + '_label', fieldLabelValue);
            }
          }
        }}
        onSearch={value => setSearchValue(value)}
        searchValue={searchValue}
      >
        {SortOptions(dependentData)?.map((object, index) => {
          return (
            <Select.Option
              value={object?.id?.id ? object.id.id : object?.id ? object.id : object?.locationId}
              key={index}
            >
              {object?.name}
              {object?.locationName}
              {object?.locationType}
            </Select.Option>
          );
        })}
      </Select>
    </Form.Item>
  );
};

SingleSelectDependentObjectAttribute.propTypes = {
  auth: PropTypes.shape({
    certificate: PropTypes.string.isRequired,
    principal: PropTypes.any,
    user: PropTypes.any,
  }).isRequired,
  attributeMetadata: PropTypes.shape({
    id: PropTypes.string.isRequired,
    child_id: PropTypes.string.isRequired,
    editable: PropTypes.bool,
    validation_rules: PropTypes.array,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    select_entity: PropTypes.string.isRequired,
    input_options_dependent: PropTypes.array.isRequired,
    input_parent_id: PropTypes.string.isRequired,
    input_parent_value: PropTypes.string,
    input_search_enable: PropTypes.bool,
    input_search_field_name: PropTypes.string,
    show_search: PropTypes.bool,
    layout: PropTypes.string,
    vertical_value_col_span: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vertical_label_height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reset_child_values_on_change: PropTypes.array,
  }).isRequired,
  formValues: PropTypes.object.isRequired,
  updateDynamicFieldValue: PropTypes.func,
};

const mapState = state => {
  const auth = selectAuth(state);
  return {auth};
};

export default connect(mapState)(SingleSelectDependentObjectAttribute);
